async function updateProductCard(swatchElement) {
  const productCard = swatchElement.closest('product-card');
  const primaryImage = productCard.querySelector('.product-card__image--primary');
  const secondaryImage = productCard.querySelector('.product-card__image--secondary');
  const productTitle = productCard.querySelector('[data-product-title]');
  const productLink = productCard.querySelector('.product-card__media');
  const priceList = productCard.querySelector('.price-list');
  const modal = productCard.querySelector('quick-buy-modal'); // Added to select quick-buy-modal
  
  // Get data from the swatch
  const imageUrl = swatchElement.dataset.imageUrl;
  const secondaryImageUrl = swatchElement.dataset.secondaryImageUrl;
  const productTitleText = swatchElement.dataset.productTitle;
  const productUrl = swatchElement.dataset.productUrl;
  const productPrice = swatchElement.dataset.productPrice;
  const productComparePrice = swatchElement.dataset.productComparePrice;
  
  // Update primary image
  if (primaryImage && imageUrl) {
    primaryImage.src = imageUrl;
    primaryImage.srcset = imageUrl;
  }
  
  // Update secondary image
  if (secondaryImage && secondaryImageUrl) {
    secondaryImage.src = secondaryImageUrl;
    secondaryImage.srcset = secondaryImageUrl;
  }
  
  // Update product title
  if (productTitle && productTitleText) {
    productTitle.textContent = productTitleText;
  }
  
  // Update product link
  if (productLink && productUrl) {
    productLink.href = productUrl;
  }
  
  // Update price
  if (priceList && productPrice) {
    const priceElement = priceList.querySelector('.price');
    if (priceElement) {
      priceElement.textContent = productPrice;
    }
    
    const comparePriceElement = priceList.querySelector('.price--compare');
    if (comparePriceElement) {
      if (productComparePrice) {
        comparePriceElement.textContent = productComparePrice;
        comparePriceElement.style.display = '';
      } else {
        comparePriceElement.style.display = 'none';
      }
    }
  }
  
  // Update quick buy modal
  if (modal && productUrl) {
    modal.setAttribute('product-url', productUrl);
  }
  
  // Update active swatch state
  const allSwatches = productCard.querySelectorAll('.color-swatch');
  allSwatches.forEach(swatch => swatch.classList.remove('selected')); // Changed from is-selected to selected
  swatchElement.classList.add('selected');
}

class ProductCardSwatches {
  constructor() {
    this.productCache = new Map();
    this.selectedSwatchData = new Map();
    this.imageUpdateLocks = new Map();
    this.init();
    this.setupEventListeners();
  }

  init() {
    document.querySelectorAll('product-card').forEach(card => {
      this.initProductCardSwatches(card);
    });
  }

  setupEventListeners() {
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('product-card').forEach(card => {
        this.initProductCardSwatches(card);
      });
    });

    this.setupMutationObserver();
    document.addEventListener('predictive-search:render', this.throttledDynamicContentUpdate.bind(this));
    document.addEventListener('filter:updated', this.throttledDynamicContentUpdate.bind(this));
    document.addEventListener('facet:updated', this.throttledDynamicContentUpdate.bind(this));
    document.addEventListener('search:updated', this.throttledDynamicContentUpdate.bind(this));
    document.addEventListener('pagination:page-changed', this.throttledDynamicContentUpdate.bind(this));
    document.addEventListener('sort:updated', this.throttledDynamicContentUpdate.bind(this));
    window.addEventListener('DOMContentLoaded', this.throttledDynamicContentUpdate.bind(this));
    window.addEventListener('load', this.throttledDynamicContentUpdate.bind(this));
  }

  throttledDynamicContentUpdate = this.throttle(() => {
    document.querySelectorAll('product-card').forEach(card => {
      this.initProductCardSwatches(card);
    });
  }, 500);

  throttle(fn, wait) {
    let lastCall = 0;
    return function (...args) {
      const now = Date.now();
      if (now - lastCall >= wait) {
        lastCall = now;
        fn.apply(this, args);
      }
    };
  }

  setupMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      let shouldProcess = false;
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.tagName && node.tagName.toLowerCase() === 'product-card') {
                shouldProcess = true;
                break;
              } else if (node.querySelectorAll) {
                const cards = node.querySelectorAll('product-card');
                if (cards.length > 0) {
                  shouldProcess = true;
                  break;
                }
              }
            }
          }
        }
        if (shouldProcess) break;
      }
      if (shouldProcess) {
        this.throttledDynamicContentUpdate();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  async initProductCardSwatches(card) {
    const swatches = card.querySelectorAll('.color-swatch[data-product-handle]');
    if (!swatches.length) return;

    const initialHandle = card.getAttribute('handle');
    const initialId = card.dataset.productId;
    this.selectedSwatchData.set(card, { handle: initialHandle, id: initialId });

    swatches.forEach(swatch => {
      const img = new Image();
      img.src = swatch.dataset.imageUrl || 'https://via.placeholder.com/600';
    });

    await this.restoreInitialState(card);

    swatches.forEach(swatch => {
      swatch.removeEventListener('click', this.handleSwatchClick);
      swatch.removeEventListener('mouseenter', this.handleSwatchHover);
      swatch.removeEventListener('mouseleave', this.handleSwatchLeave);
      swatch.addEventListener('click', this.debouncedHandleSwatchClick);
      swatch.addEventListener('mouseenter', this.debouncedHandleSwatchHover);
      swatch.addEventListener('mouseleave', this.handleSwatchLeave);
    });

    if (swatches.length > 0) {
      const firstSwatch = swatches[0];
      firstSwatch.click();
    }
  }
  
  debouncedHandleSwatchClick = this.debounce(this.handleSwatchClick.bind(this), 100);
  debouncedHandleSwatchHover = this.debounce(this.handleSwatchHover.bind(this), 100);

  debounce(fn, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  handleSwatchClick = async (event) => {
    event.preventDefault();
    const swatch = event.currentTarget;
    if (swatch.classList.contains('selected')) return;

    const card = swatch.closest('product-card');
    const productHandle = swatch.dataset.productHandle;
    const imageUrl = swatch.dataset.imageUrl || 'https://via.placeholder.com/600';
    const productTitle = swatch.dataset.productTitle;
    const productUrl = swatch.dataset.productUrl;
    const productPrice = swatch.dataset.productPrice;
    const productComparePrice = swatch.dataset.productComparePrice;
    const productId = swatch.dataset.productId;

    if (!productHandle || !card || !imageUrl || !productTitle || !productUrl || !productId) {
      console.error('Missing swatch data:', { productHandle, imageUrl, productTitle, productUrl, productId });
      return;
    }

    this.selectedSwatchData.set(card, { handle: productHandle, id: productId });

    card.classList.add('is-loading');

    try {
      await this.timeoutPromise(this.updateProductImage(card, imageUrl), 10000, 'Image load timed out');
      await this.timeoutPromise(this.updateSizeSelector(card, productId, productHandle), 10000, 'Size selector update timed out');

      this.updateProductLinkAndTitle(card, productHandle, productTitle, productUrl);
      this.updateProductPrice(card, productPrice, productComparePrice);
      this.highlightSelectedSwatch(card, swatch);

      const modal = card.querySelector('quick-buy-modal');
      if (modal) {
        modal.setAttribute('product-url', productUrl);
      }
    } catch (error) {
      console.error('Error updating product card:', error);
    } finally {
      card.classList.remove('is-loading');
    }
  };

  handleSwatchHover = async (event) => {
    const swatch = event.currentTarget;
    const card = swatch.closest('product-card');
    const imageUrl = swatch.dataset.imageUrl || 'https://via.placeholder.com/600';

    if (!card || !imageUrl) {
      console.error('Missing swatch data for hover:', { imageUrl });
      return;
    }

    try {
      await this.timeoutPromise(this.updateProductImage(card, imageUrl), 10000, 'Image load timed out on hover');
    } catch (error) {
      console.error('Error updating images on hover:', error);
    }
  };

  handleSwatchLeave = async (event) => {
    const card = event.currentTarget.closest('product-card');
    await this.restoreInitialState(card);
  };

  timeoutPromise(promise, ms, errorMessage) {
    return Promise.race([
      promise,
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error(errorMessage)), ms);
      })
    ]);
  }

  updateProductImage(card, primaryImageUrl) {
    return new Promise((resolve, reject) => {
      const mediaContainer = card.querySelector('.product-card__media');
      if (!mediaContainer) {
        console.warn('No media container found for image');
        return resolve();
      }

      const existingPrimaryImage = mediaContainer.querySelector('.product-card__image--primary');
      if (!existingPrimaryImage) {
        console.warn('No existing primary image found to copy styles from');
        return resolve();
      }

      const cardId = card.dataset.productId;
      if (this.imageUpdateLocks.get(cardId)) {
        console.warn('Image update already in progress for card:', cardId);
        return resolve();
      }
      this.imageUpdateLocks.set(cardId, true);

      const existingStyles = window.getComputedStyle(existingPrimaryImage);
      const newPrimaryImage = new Image();
      newPrimaryImage.className = existingPrimaryImage.className;
      newPrimaryImage.alt = existingPrimaryImage.alt;
      newPrimaryImage.loading = 'eager';
      newPrimaryImage.fetchpriority = 'auto';
      newPrimaryImage.sizes = existingPrimaryImage.sizes;
      newPrimaryImage.style.width = existingStyles.width;
      newPrimaryImage.style.height = existingStyles.height;
      newPrimaryImage.style.objectFit = existingStyles.objectFit;
      newPrimaryImage.style.maxWidth = existingStyles.maxWidth;
      newPrimaryImage.style.maxHeight = existingStyles.maxHeight;
      Array.from(existingPrimaryImage.attributes)
        .filter(attr => attr.name.startsWith('data-'))
        .forEach(attr => {
          newPrimaryImage.setAttribute(attr.name, attr.value);
        });
      newPrimaryImage.src = primaryImageUrl;

      newPrimaryImage.onload = () => {
        try {
          if (mediaContainer.contains(existingPrimaryImage)) {
            mediaContainer.replaceChild(newPrimaryImage, existingPrimaryImage);
          } else {
            console.warn('Primary image is no longer a child of media container, updating src instead');
            existingPrimaryImage.src = primaryImageUrl;
            existingPrimaryImage.srcset = primaryImageUrl;
          }
          this.imageUpdateLocks.delete(cardId);
          resolve();
        } catch (error) {
          console.error('Error replacing image:', error);
          this.imageUpdateLocks.delete(cardId);
          reject(error);
        }
      };

      newPrimaryImage.onerror = () => {
        console.warn('Failed to load primary image:', primaryImageUrl);
        newPrimaryImage.src = 'https://via.placeholder.com/600';
        try {
          if (mediaContainer.contains(existingPrimaryImage)) {
            mediaContainer.replaceChild(newPrimaryImage, existingPrimaryImage);
          } else {
            existingPrimaryImage.src = newPrimaryImage.src;
            existingPrimaryImage.srcset = newPrimaryImage.src;
          }
          this.imageUpdateLocks.delete(cardId);
          resolve();
        } catch (error) {
          console.error('Error replacing fallback image:', error);
          this.imageUpdateLocks.delete(cardId);
          reject(error);
        }
      };
    });
  }

  updateProductLinkAndTitle(card, handle, title, url) {
    const productLink = card.querySelector('.product-card__media');
    const titleElement = card.querySelector('.product-title[data-product-title]');
    if (productLink) productLink.href = url;
    if (titleElement) {
      titleElement.textContent = title;
      titleElement.href = url;
    }
    card.setAttribute('handle', handle);
  }

  updateProductPrice(card, price, comparePrice) {
    const priceList = card.querySelector('.price-list');
    if (!priceList) return;

    priceList.innerHTML = '';

    const priceElement = document.createElement('span');
    priceElement.className = 'price';
    priceElement.textContent = price;
    priceList.appendChild(priceElement);

    if (comparePrice && comparePrice !== '') {
      const comparePriceElement = document.createElement('span');
      comparePriceElement.className = 'price--compare';
      comparePriceElement.textContent = comparePrice;
      priceList.appendChild(comparePriceElement);
    }
  }

  async updateSizeSelector(card, productId, productHandle) {
    const sizeSelector = card.querySelector('.product-card__floating-size-selector');
    if (!sizeSelector) return;

    try {
      let productData;
      if (this.productCache.has(productHandle)) {
        productData = this.productCache.get(productHandle);
      } else {
        const response = await fetch(`/products/${productHandle}.js`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        productData = await response.json();
        this.productCache.set(productHandle, productData);
      }

      sizeSelector.innerHTML = '';

      productData.variants.forEach(variant => {
        const productForm = document.createElement('product-form');
        const form = document.createElement('form');
        form.method = 'post';
        form.action = '/cart/add';
        form.className = 'form';

        const quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        quantityInput.value = '1';

        const idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = 'id';
        idInput.value = variant.id;

        form.appendChild(quantityInput);
        form.appendChild(idInput);

        let classList = 'floating-size-selector__button';
        let disabled = !variant.available;
        if (!variant.available) classList += ' is-disabled';

        const button = document.createElement('button');
        button.type = 'submit';
        button.textContent = variant.title;
        button.className = classList;
        button.disabled = disabled;
        button.style.setProperty('--color-background', 'var(--color-background, #fff)');
        button.style.setProperty('--color-text', 'var(--color-text, #000)');
        button.style.width = '100%';

        form.appendChild(button);
        productForm.appendChild(form);
        sizeSelector.appendChild(productForm);
      });
    } catch (error) {
      console.error('Error updating size selector for handle:', productHandle, error);
      throw error;
    }
  }

  async restoreInitialState(card) {
    const mediaContainer = card.querySelector('.product-card__media');
    const priceList = card.querySelector('.price-list');
    const titleElement = card.querySelector('.product-title[data-product-title]');
    const productLink = card.querySelector('.product-card__media');

    const selectedData = this.selectedSwatchData.get(card) || { handle: card.getAttribute('handle'), id: card.dataset.productId };
    const productHandle = selectedData.handle;
    const productId = selectedData.id;

    try {
      let productData;
      if (this.productCache.has(productHandle)) {
        productData = this.productCache.get(productHandle);
      } else {
        const response = await fetch(`/products/${productHandle}.js`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        productData = await response.json();
        this.productCache.set(productHandle, productData);
      }

      const selectedSwatch = card.querySelector(`.color-swatch[data-product-handle="${productHandle}"]`);
      const primaryImageUrl = selectedSwatch ? selectedSwatch.dataset.imageUrl : productData.featured_image || 'https://via.placeholder.com/600';
      const initialTitle = selectedSwatch ? selectedSwatch.dataset.productTitle : productData.title;

      const existingPrimaryImage = mediaContainer.querySelector('.product-card__image--primary');
      if (existingPrimaryImage && primaryImageUrl) {
        const cardId = card.dataset.productId;
        if (this.imageUpdateLocks.get(cardId)) {
          console.warn('Image update already in progress for card:', cardId);
          return;
        }
        this.imageUpdateLocks.set(cardId, true);

        const existingStyles = window.getComputedStyle(existingPrimaryImage);
        const newPrimaryImage = new Image();
        newPrimaryImage.className = existingPrimaryImage.className;
        newPrimaryImage.alt = existingPrimaryImage.alt;
        newPrimaryImage.loading = 'eager';
        newPrimaryImage.fetchpriority = 'auto';
        newPrimaryImage.sizes = existingPrimaryImage.sizes;
        newPrimaryImage.style.width = existingStyles.width;
        newPrimaryImage.style.height = existingStyles.height;
        newPrimaryImage.style.objectFit = existingStyles.objectFit;
        newPrimaryImage.style.maxWidth = existingStyles.maxWidth;
        newPrimaryImage.style.maxHeight = existingStyles.maxHeight;
        Array.from(existingPrimaryImage.attributes)
          .filter(attr => attr.name.startsWith('data-'))
          .forEach(attr => {
            newPrimaryImage.setAttribute(attr.name, attr.value);
          });
        newPrimaryImage.src = primaryImageUrl;

        newPrimaryImage.onload = () => {
          try {
            if (mediaContainer.contains(existingPrimaryImage)) {
              mediaContainer.replaceChild(newPrimaryImage, existingPrimaryImage);
            } else {
              console.warn('Primary image is no longer a child of media container, updating src instead');
              existingPrimaryImage.src = primaryImageUrl;
              existingPrimaryImage.srcset = primaryImageUrl;
            }
            this.imageUpdateLocks.delete(cardId);
          } catch (error) {
            console.error('Error replacing image in restoreInitialState:', error);
            this.imageUpdateLocks.delete(cardId);
          }
        };
        newPrimaryImage.onerror = () => {
          console.warn('Failed to load primary image in restoreInitialState:', primaryImageUrl);
          newPrimaryImage.src = 'https://via.placeholder.com/600';
          try {
            if (mediaContainer.contains(existingPrimaryImage)) {
              mediaContainer.replaceChild(newPrimaryImage, existingPrimaryImage);
            } else {
              existingPrimaryImage.src = newPrimaryImage.src;
              existingPrimaryImage.srcset = newPrimaryImage.src;
            }
            this.imageUpdateLocks.delete(cardId);
          } catch (error) {
            console.error('Error replacing fallback image in restoreInitialState:', error);
            this.imageUpdateLocks.delete(cardId);
          }
        };
      }

      if (priceList) {
        const initialPrice = priceList.dataset.initialPrice;
        const initialComparePrice = priceList.dataset.initialComparePrice;
        priceList.innerHTML = '';
        const priceElement = document.createElement('span');
        priceElement.className = 'price';
        priceElement.textContent = initialPrice;
        priceList.appendChild(priceElement);
        if (initialComparePrice && initialComparePrice !== '') {
          const comparePriceElement = document.createElement('span');
          comparePriceElement.className = 'price--compare';
          comparePriceElement.textContent = initialComparePrice;
          priceList.appendChild(comparePriceElement);
        }
      }

      if (titleElement && productLink && productData) {
        titleElement.textContent = initialTitle;
        titleElement.href = productData.url || `/products/${productHandle}`;
        productLink.href = productData.url || `/products/${productHandle}`;
      }

      await this.updateSizeSelector(card, productId, productHandle);
    } catch (error) {
      console.error('Error restoring initial state:', error);
    }
  }

  highlightSelectedSwatch(card, selectedSwatch) {
    card.querySelectorAll('.color-swatch').forEach(swatch => {
      swatch.classList.remove('selected');
      swatch.setAttribute('aria-selected', 'false');
    });
    selectedSwatch.classList.add('selected');
    selectedSwatch.setAttribute('aria-selected', 'true');
  }
}

let productCardSwatchesInstance = null;

function initProductCardSwatches() {
  if (productCardSwatchesInstance) {
    productCardSwatchesInstance.throttledDynamicContentUpdate();
  } else {
    productCardSwatchesInstance = new ProductCardSwatches();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initProductCardSwatches();
});

document.addEventListener('shopify:section:load', () => {
  initProductCardSwatches();
});

document.addEventListener('click', (event) => {
  if (
    event.target.closest('[data-filter-item]') ||
    event.target.closest('[data-facet-item]') ||
    event.target.closest('.collection-filters__sort') ||
    event.target.closest('.filter-group')
  ) {
    setTimeout(() => {
      initProductCardSwatches();
    }, 500);
  }
});

let lastUrl = location.href;
new MutationObserver(() => {
  const url = location.href;
  if (url !== lastUrl) {
    lastUrl = url;
    setTimeout(() => {
      initProductCardSwatches();
    }, 500);
  }
}).observe(document, { subtree: true, childList: true });